import React from 'react';
import { StyleSheet } from 'react-native';

import { log } from '../../utils/logger';
import { mapStyles } from './mapStyles';

const MAP_TYPES = {
  STANDARD: 'standard',
  SATELLITE: 'satellite',
  HYBRID: 'hybrid',
  TERRAIN: 'terrain',
  NONE: 'none',
  MUTEDSTANDARD: 'mutedStandard',
};

/**
 * This is a platform-agnostic wrapper around the react-native-maps and react-native-web-maps
 * libraries. It correctly applies custom styles to each environment. See react-native-maps
 * for `props` definitions.
 */
export const MapViewMobile = (props) => {
  const { initialRegion, onRegionChangeComplete, onPress, children, mapViewRef, mapViewImpl } = props;

  if (!mapViewImpl) {
    throw new Error('MapViewMobile requires mapViewImpl prop to be provided');
  }

  const MapViewImpl = mapViewImpl;

  const _onRegionChangeComplete = (region) => {
    const MAX_PRECISION = 3;
    // On Android devices region coordinates are returned with very high precision: 14 significant digits
    // (e.g. 39.58744648744947).  But due to some quirk in the Google Maps provider, the deltas are - by observation -
    // only stable to about 7 significant digits; from the logs:
    //
    // As a result the region is being continually updated as each attempt to set a region based on this callback
    // potentially triggers a state update, causing an infinite cycle of requests.  To mitigate this, the maximum
    // precision returned the coordinates of a region are capped below what appears to be threshold at which the
    // precision becomes unstable.
    //
    // And the obligatory XKCD, courtesy of bryan@: https://xkcd.com/2170/
    //
    // Because this actually only affects the state management in (and thus the rendering of the PantryMap), it would
    // normally be preferablle to make this change in that component, rather than in this one that shouldn't have
    // implicit knowledge of how it is being used.  However, because the MapView* family of components have proven to be
    // necessary to work around the differences in how regions are managed on different platforms (web vs mobile),
    // PantryMap is forced to treat the definition of a region (the deltas in particular) as opaque and
    // implementation-specific.  Thus this tweak lives here.

    return onRegionChangeComplete(
      new MapViewMobileRegion(
        parseFloat(region.latitude.toFixed(MAX_PRECISION)),
        parseFloat(region.latitudeDelta.toFixed(MAX_PRECISION)),
        parseFloat(region.longitude.toFixed(MAX_PRECISION)),
        parseFloat(region.longitudeDelta.toFixed(MAX_PRECISION))
      )
    );
  };

  return (
    <MapViewImpl
      customMapStyle={mapStyles}
      initialRegion={initialRegion}
      mapType={MAP_TYPES.STANDARD}
      onPress={onPress}
      onRegionChangeComplete={_onRegionChangeComplete}
      ref={mapViewRef}
      showsUserLocation={true}
      showsMyLocationButton={false}
      style={styles.mapStyle}
      toolbarEnabled={false}
    >
      {children}
    </MapViewImpl>
  );
};

export class MapViewMobileRegion {
  constructor(latitude, latitudeDelta, longitude, longitudeDelta) {
    this.latitude = latitude;
    this.latitudeDelta = latitudeDelta;
    this.longitude = longitude;
    this.longitudeDelta = longitudeDelta;

    this.identity = [this.latitude, this.latitudeDelta, this.longitude, this.longitudeDelta].join('|');
  }

  asNativeRegion() {
    return {
      latitude: this.latitude,
      latitudeDelta: this.latitudeDelta,
      longitude: this.longitude,
      longitudeDelta: this.longitudeDelta,
    };
  }

  center(latitude, longitude) {
    return new MapViewMobileRegion(latitude, this.latitudeDelta, longitude, this.longitudeDelta);
  }
}

const styles = StyleSheet.create({
  mapStyle: {
    flex: 1,
  },
  locationCross: {
    width: 56,
    height: 56,
    position: 'absolute',
    bottom: 36,
    left: 16,
  },
});
